import { convertSelectRangeOptionsToMap } from 'pages/Admin/AdminTools/Configuration/LoanFieldsProperties/utils'
import { LienTypeOption } from 'pages/LoanStructure/interfaces'
import type { IRange } from 'pages/Resources'
import { isEmpty } from 'utils'

import { IS_NEXYS_COMPANY, IS_PRODUCTION } from './company.constants'
import type { InputType } from './input.type.constants'
import { isCustomFieldKey } from './loan.input.fields.constants'
import { IVisibleProp } from './loan.input.visibility.type'

export const finresiMapConstants: Record<string, any> = {
  citizen: {
    UsCitizen: 'US Citizen',
    PermanentResidentAlien: 'Permanent Resident',
    NonPermanentResidentAlien: 'Non Permanent Resident',
    ForeignNational: 'Foreign National',
    ITIN: 'ITIN',
  },
}

export const EscrowTypeLabels: Record<string, string> = {
  notWaived: 'Not Waived',
  waiveBoth: 'Waive Both',
  waiveInsurance: 'Waive Insurance',
}

export const DscrRangeOptions: Record<number, string> = {
  1: '0 - 0.74',
  2: '0.75 - 0.99',
  3: '1 - 1.09',
  4: '1.1 - 1.14',
  5: '1.15 - 1.25',
  6: '1.26 - 1.49',
  7: '1.5 - 10',
}

export const DscrRangeMapping: Record<number, IRange> = {
  1: { from: 0, to: 0.74 },
  2: { from: 0.75, to: 0.99 },
  3: { from: 1, to: 1.09 },
  4: { from: 1.1, to: 1.14 },
  5: { from: 1.15, to: 1.25 },
  6: { from: 1.26, to: 1.49 },
  7: { from: 1.5, to: 10 },
}

export const MINVALUE: number = 999999999

export const enum ProductTypes {
  Bridge = 'Bridge',
  FixAndFlip = 'Fix and Flip',
  GroundUpConstruction = 'Ground Up Construction',
  DSCR = 'DSCR (Long Term Rental)',
}

export const productTypeOptions = ['Bridge', 'Fix and Flip', 'Ground Up Construction', 'DSCR (Long Term Rental)']

export const productTypeOptionsMap: Record<string, string> = {
  Bridge: 'Bridge',
  'Fix and Flip': 'Fix & Flip / Fix to Rent',
  'Ground Up Construction': 'Ground Up Construction',
  'DSCR (Long Term Rental)': 'DSCR (Rental Loan)',
}

export const lockTypeOptions = ['Locked', 'Float', 'Expired']

export const channelTypeOptions = ['Wholesale', 'Correspondent', 'Retail', 'Table Funder', 'Brokered']

export const transactionTypeOptions = ['Purchase', 'Refinance']

export const howHearAboutUsOpions = ['Rehab Wallet', 'Networking Event', 'Referral', 'Social Media/Website', 'Other']

export const preferredCommunicationOptions = ['Phone', 'Email', 'Text', 'Web Portal']

export const loanCustomRangeOptionFields: string[] = [
  'foreclosure',
  'deedInLieu',
  'shortSale',
  'experience',
  'forbearanceLoanModification',
  'bankruptcy',
  'mortgageLates',
]
export const loanCustomSelectOptionFields: string[] = ['loanTerm', 'prepaymentPenalty']
export const editableOptionFields: string[] = ['propertyType', 'proposedOccupancy', 'borrowerType']

export const loanOptionsConstants = {
  entityType: [
    'Limited Liability Company',
    'Limited Liability Partnership',
    'Corporation',
    'Partnership',
    'Individual',
  ],
  productType: productTypeOptionsMap,
  transactionType: transactionTypeOptions,
  loanTerm: {
    6: '6 Months',
    12: '12 Months',
    18: '18 Months',
    24: '24 Months',
  },
  dscrLoanTerm: {
    180: '15 Years',
    240: '20 Years',
    360: '30 Years',
    480: '40 Years',
  },
  escrowType: ['Not Waived', 'Waive Both', 'Waive Insurance'],
  borrowerType: ['LLC', 'LLP', 'Corp', 'Partnership', 'Revocable Trust', 'Individual'],
  proposedOccupancy: ['Owner Occupied', 'Non Owner Occupied', 'Partially Owner Occupied', 'Vacant', 'Partially Vacant'],
  prepaymentLong: {
    '0-0': '0 Month',
    '12-5': '12 Months',
    '24-5/5': '24 Months',
    '36-5/5/5': '36 Months',
    '48-5/5/5/5': '48 Months',
    '60-5/5/5/5/5': '60 Months',
  },
  propertyType: [
    'SFR-Detached',
    'SFR-Attached',
    'Condo',
    '2-4 Units',
    'PUD',
    'Mixed-Use',
    'Modular',
    'Multifamily 5+',
    'Industrial',
    'Manufactured',
    'Self Storage',
    'Office',
    'Retail',
    'Warehouse',
    'Automotive',
  ],
  bankruptcy: {
    'null-null-null': 'None',
    '0-null-null': 'BK Active',
    '7-1-11': 'BK7 Discharged 1-11 Months',
    '7-12-23': 'BK7 Discharged 12-23 Months',
    '7-24-35': 'BK7 Discharged 24-35 Months',
    '7-36-47': 'BK7 Discharged 36-47 Months',
    '7-48-null': 'BK7 Discharged 48+ Months',
    '13-1-11': 'BK13 Discharged 1-11 Months',
    '13-12-23': 'BK13 Discharged 12-23 Months',
    '13-24-35': 'BK13 Discharged 24-35 Months',
    '13-36-47': 'BK13 Discharged 36-47 Months',
    '13-48-null': 'BK13 Discharged 48+ Months',
  },
  loannex2: {
    'null-null': 'None',
    '1-11': '1-11 Months',
    '12-23': '12-23 Months',
    '24-35': '24-35 Months',
    '36-47': '36-47 Months',
    '48-null': '48+ Months',
  },
  mortgageLates: {
    '0-0-30-12': '0X30X12',
    '1-1-30-12': '1X30X12',
    '0-0-60-12': '0X60X12',
    '1-null-60-12': '>= 1X60X12',
  },
  fullMortgageLates: {
    '1X30X12': '30 days late in the last 12 months.',
    '>= 2X30X12': '30 days late multiple times in the last 12 months.',
    '1X60X12': '60 days late in the last 12 months.',
    '>= 2X60X12': '60 days late multiple times in the last 12 months.',
    '1X90X12': 'More than 60 days late in the last 12 months.',
    '>= 2X90X12': 'More than 60 days late multiple times in the last 12 months.',
    '1X30X24': '30 days late in the last 24 months.',
    '>= 2X30X24': '30 days late multiple times in the last 24 months.',
    '1X60X24': '60 days late in the last 24 months.',
    '>= 2X60X24': '60 days late multiple times in the last 24 months.',
    '1X90X24': 'More than 60 days late in the last 24 months.',
    '>= 2X90X24': 'More than 60 days late multiple times in the last 24 months.',
  },
  forbearance: { 'null-null': 'None', 'null-6': '<=6 Months', '7-24': '7-24 Months', '25-null': '25+ Months' },
  states: [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DC',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'MA',
    'ME',
    'MD',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VA',
    'VI',
    'VT',
    'WA',
    'WV',
    'WI',
    'WY',
  ],
  hmda: {
    1: 'Provided By Borrower',
    2: 'Not Provided In Mail Telephone Or Internet Application',
    3: 'Collected On The Basis Of Visual Observation Or Surname',
  },
  citizenship: ['US Citizen', 'Permanent Resident', 'Non Permanent Resident', 'Foreign National', 'ITIN'],
  experience: {
    '0-0': '0 Transaction',
    '1-1': '1 Transaction',
    '2-2': '2 Transactions',
    '3-3': '3 Transactions',
    '4-4': '4 Transactions',
    '5-null': '5+ Transactions',
  },
}

export const convertValueToLabel = (key: string, value: any, fieldProperties: Record<string, IVisibleProp>) => {
  let label = isEmpty(value) ? '' : String(value)

  const fieldOptions =
    fieldProperties && fieldProperties[key]?.options && !!fieldProperties[key]?.options?.length
      ? convertSelectRangeOptionsToMap(fieldProperties[key].options)
      : {}

  switch (key) {
    case 'productType':
      label = (loanOptionsConstants.productType as any)[value]
      break
    case 'lienType':
      label = (LienTypeOption as any)[value]
      break
    case 'loanTerm':
      label =
        fieldOptions[value] ||
        (loanOptionsConstants.loanTerm as any)[value] ||
        (loanOptionsConstants.dscrLoanTerm as any)[value]
      break
    case 'experience':
      label = fieldOptions[value] || (loanOptionsConstants.experience as any)[value]
      break
    case 'foreclosure':
    case 'deedInLieu':
    case 'shortSale':
      label = fieldOptions[value] || (loanOptionsConstants.loannex2 as any)[value]
      break
    case 'mortgageLates':
      label = fieldOptions[value] || (loanOptionsConstants.mortgageLates as any)[value]
      break
    case 'forbearanceLoanModification':
      label = fieldOptions[value] || (loanOptionsConstants.forbearance as any)[value]
      break
    case 'prepaymentPenalty':
      label = fieldOptions[value] || (loanOptionsConstants.prepaymentLong as any)[value]
      break
    case 'bankruptcy':
      label = fieldOptions[value] || (loanOptionsConstants.bankruptcy as any)[value]
      break
    case 'propertyUnderConstruction':
    case 'isPropertyInLeasableState':
    case 'firstTimeHomeBuyer':
    case 'firstTimeHomeInvestor':
    case 'ruralProperty':
    case 'propertyHasBeenListed':
    case 'isShortTermRental':
    case 'decliningMarketProperty':
    case 'isCEMATransaction':
    case 'isNewConstructionProperty':
    case 'changeToSquareFootage':
    case 'isNoScore':
    case 'isMultipleProperties':
    case 'isBPiece':
      label = isEmpty(value) ? '' : !!value ? 'Yes' : 'No'
      break
  }

  if (isCustomFieldKey(key)) {
    switch (fieldProperties[key].inputType) {
      case 'ToggleButton':
        label = isEmpty(value) ? '' : !!value ? 'Yes' : 'No'
        break
      case 'Select':
        label = (fieldProperties[key].options as any)[value] || ''
        break
    }
  }

  return label
}

export const getLoanFieldOption = (key: string, fieldProperties: Record<string, IVisibleProp>) => {
  const fieldOptions =
    fieldProperties && fieldProperties[key]?.options && !!fieldProperties[key]?.options?.length
      ? convertSelectRangeOptionsToMap(fieldProperties[key].options)
      : {}

  switch (key) {
    case 'productType':
      return loanOptionsConstants.productType as Record<string, any>
    case 'lienType':
      return LienTypeOption as Record<string, any>
    case 'loanTerm':
      return (fieldOptions || loanOptionsConstants.loanTerm || loanOptionsConstants.dscrLoanTerm) as Record<string, any>
    case 'experience':
      return (fieldOptions || loanOptionsConstants.experience) as Record<string, any>
    case 'foreclosure':
    case 'deedInLieu':
    case 'shortSale':
      return (fieldOptions || loanOptionsConstants.loannex2) as Record<string, any>
    case 'mortgageLates':
      return (fieldOptions || loanOptionsConstants.mortgageLates) as Record<string, any>
    case 'forbearanceLoanModification':
      return (fieldOptions || loanOptionsConstants.forbearance) as Record<string, any>
    case 'prepaymentPenalty':
      return (fieldOptions || loanOptionsConstants.prepaymentLong) as Record<string, any>
    case 'bankruptcy':
      return (fieldOptions || loanOptionsConstants.bankruptcy) as Record<string, any>
  }

  return {}
}

export const getInitialLoanFields = (forPdf: boolean = false) => {
  let rlt: Record<string, InputType> = {
    productType: {
      title: 'Product Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.productType,
      error: '',
      required: true,
    },
    transactionType: {
      title: 'Transaction Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.transactionType,
      error: '',
      required: true,
    },
    lienType: {
      title: 'Lien Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: LienTypeOption,
      error: '',
      required: true,
    },
    isMultipleProperties: {
      title: 'Financing of Additional Properties?',
      inputType: 'check',
      error: '',
      required: true,
    },
    howManyProperties: {
      key: 'howManyProperties',
      title: 'How Many Properties?',
      inputType: 'text',
      type: 'number',
      error: '',
      required: true,
    },
    propertyUnderConstruction: {
      title: 'Property Under Construction?',
      inputType: 'check',
      error: '',
      required: true,
    },
    isPropertyInLeasableState: {
      title: 'Can property be rented as is?',
      inputType: 'check',
      error: '',
      required: true,
    },
    isShortTermRental: {
      title: 'Is the property a short term rental?',
      inputType: 'check',
      error: '',
      required: true,
    },
    decliningMarketProperty: {
      title: 'Is the property located in a declining market?',
      inputType: 'check',
      error: '',
      required: true,
    },
    isNewConstructionProperty: {
      title: 'Is the property a new construction?',
      inputType: 'check',
      error: '',
      required: true,
    },
    changeToSquareFootage: {
      title: 'Will there be a change to Square Footage?',
      inputType: 'check',
      error: '',
      required: true,
    },
    proposedOccupancy: {
      title: 'Occupancy',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.proposedOccupancy,
      error: '',
      required: true,
    },
    loanTerm: {
      title: 'Loan Term',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.loanTerm,
      error: '',
      required: true,
    },
    borrowerType: {
      title: 'Borrower Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.borrowerType,
      error: '',
      required: true,
    },
    proposedLoanAmount: {
      title: 'Proposed Loan Amount',
      inputType: 'text',
      key: 'proposedLoanAmount',
      type: 'thousandSepNoDecimal',
      error: '',
      prefix: '$',
      required: true,
    },
    isBPiece: {
      title: 'B Piece?',
      inputType: 'check',
      error: '',
      required: true,
    },
    aPieceAmount: {
      title: 'A Piece',
      inputType: 'text',
      type: 'thousandSepNoDecimal',
      error: '',
      prefix: '$',
      required: true,
    },
    propertyPurchasePrice: {
      title: 'Property Purchase Price',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    asIsValue: {
      title: 'As Is Appraised Value', // 'As Is Value', Original Label
      key: 'asIsValue',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    afterRepairValue: {
      title: 'After Repair Value',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    rehabBudget: {
      title: 'Construction Budget',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    constructionReserve: {
      title: 'Construction Amount Financed',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    interestReserve: {
      title: 'Interest Reserve',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
      tooltip: 'The dollar amount of interest payment(s) being financed into the loan amount.',
    },
    experience: {
      title: 'Experience',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.experience,
      error: '',
      required: true,
    },
    howManyExperiences: {
      title: 'How Many Experiencies?',
      inputType: 'select',
      hasDefaultOption: true,
      options: {},
      error: '',
      required: true,
    },
    floodInsurance: {
      title: 'Flood Insurance',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    schoolTax: {
      title: 'School Tax',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    otherTax: {
      title: 'Other Tax',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    proposedMonthlyTaxes: {
      title: 'Proposed Monthly Taxes',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    proposedMonthlyInsurance: {
      title: 'Proposed Monthly Insurance',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    proposedMonthlyRent: {
      title: 'Proposed Monthly Rent',
      key: 'proposedMonthlyRent',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    proposedMonthlyHoaDues: {
      title: 'Proposed Monthly HOA Dues',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    propertyType: {
      title: 'Property Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.propertyType,
      error: '',
      required: true,
    },
    condoType: {
      title: 'Condo Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: ['Warrantable', 'Non-Warrantable', 'Condotel'],
      error: '',
      required: true,
    },
    prepaymentPenalty: {
      title: 'Prepayment Penalty',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.prepaymentLong,
      error: '',
      required: true,
    },
    propertyHasBeenListed: {
      title: 'Has property been listed in the last 6 months?',
      inputType: 'check',
      error: '',
      required: true,
    },
    residency: {
      title: 'Citizenship',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.citizenship,
      error: '',
      required: true,
    },
    amortizationType: {
      title: 'Amortization Type',
      inputType: 'select',
      key: 'amortizationType',
      hasDefaultOption: true,
      options: ['ARM', 'Fixed', 'IO - ARM', 'IO - Fixed'],
      error: '',
      required: true,
    },
    escrowType: {
      title: 'Escrow Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.escrowType,
      error: '',
      required: true,
    },
    firstTimeHomeBuyer: {
      title: 'First Time Home Buyer?',
      inputType: 'check',
      error: '',
      required: true,
    },
    firstTimeHomeInvestor: {
      title: 'First Time Home Investor?',
      inputType: 'check',
      error: '',
      required: true,
    },
    ruralProperty: {
      title: 'Rural Property?',
      inputType: 'check',
      error: '',
      required: true,
    },
    bankruptcy: {
      title: 'Bankruptcy',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.bankruptcy,
      error: '',
      required: true,
    },
    foreclosure: {
      title: 'Foreclosure',
      inputType: 'select',
      hasDefaultOption: true,
      options: { ...loanOptionsConstants.loannex2 },
      error: '',
      required: true,
    },
    deedInLieu: {
      title: 'Deed-In-Lieu',
      inputType: 'select',
      hasDefaultOption: true,
      options: { ...loanOptionsConstants.loannex2 },
      error: '',
      required: true,
    },
    shortSale: {
      title: 'Short Sale',
      inputType: 'select',
      hasDefaultOption: true,
      options: { ...loanOptionsConstants.loannex2 },
      error: '',
      required: true,
    },
    mortgageLates: {
      title: 'Mortgage Lates',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.mortgageLates,
      error: '',
      required: true,
    },
    forbearanceLoanModification: {
      title: 'Forbearance / Loan Modification',
      inputType: 'select',
      hasDefaultOption: true,
      options: loanOptionsConstants.forbearance,
      error: '',
      required: true,
    },
    secondLien: {
      title: 'Second Lien',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    secondLienPayment: {
      title: 'Second Lien Payment',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    lienPayoff: {
      title: 'Lien Payoff',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    closingCostEstimate: {
      title: 'Closing Cost Estimate',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
      readOnly: IS_PRODUCTION && !IS_NEXYS_COMPANY ? false : true, // Enable to Pull Closing Cost Estimate
    },
    escrowRepairHoldback: {
      title: 'Escrow Repair Holdback',
      inputType: 'text',
      type: 'thousandSep',
      error: '',
      prefix: '$',
      required: true,
    },
    monthsReserve: {
      title: 'Months Reserve',
      inputType: 'text',
      type: 'number',
      error: '',
      required: true,
    },
    estimatedDscr: {
      title: 'DSCR',
      inputType: 'select',
      hasDefaultOption: true,
      options: DscrRangeOptions,
      error: '',
      required: true,
      visible: false,
    },
    numberOfUnits: {
      title: 'Number of Units',
      inputType: 'text',
      type: 'number',
      error: '',
      required: true,
    },
    borrowerFirstName: {
      title: 'Guarantor First Name',
      inputType: 'text',
      error: '',
      required: true,
    },
    borrowerMiddleName: {
      title: 'Guarantor Middle Name',
      inputType: 'text',
      error: '',
    },
    borrowerLastName: {
      title: 'Guarantor Last Name',
      inputType: 'text',
      error: '',
      required: true,
    },
    isNoScore: {
      title: 'No Score',
      inputType: 'checkbox',
      color: 'gray',
      className: 'mt-6',
      error: '',
    },
    estimatedCreditScore: {
      title: 'Credit Score',
      key: 'estimatedCreditScore',
      inputType: 'text',
      type: 'creditScore',
      error: '',
      required: true,
    },
    propertyState: {
      title: 'State',
      inputType: 'select',
      hasDefaultOption: true,
      options: [...loanOptionsConstants.states],
      error: '',
      required: true,
    },
    isCEMATransaction: {
      title: 'Is this transaction a CEMA?',
      inputType: 'check',
      error: '',
      required: true,
    },
    subjectPropertyAddress: {
      title: 'Subject Property Address',
      inputType: 'Map',
      sitexPro: true,
      error: '',
      required: true,
      length: 1,
    },
    additionalPropertyAddress: {
      title: 'Additional Subject Property Address',
      inputType: 'AutoComplete',
      autoCompleteType: 'map',
      error: '',
      length: 1,
    },
    subjectPropertyMap: {
      title: 'Subject Property Address Map',
      inputType: 'GoogleMap',
      length: 1,
    },
    ltvAdjustment: {
      title: 'LTV Adjustment',
      inputType: 'text',
      type: 'number',
      error: '',
    },
    cltvAdjustment: {
      title: 'CLTV Adjustment',
      inputType: 'text',
      type: 'number',
      error: '',
    },
    aivLtvAdjustment: {
      title: 'AIV-LTV Adjustment',
      inputType: 'text',
      type: 'number',
      error: '',
    },
    arvLtvAdjustment: {
      title: 'ARV-LTV Adjustment',
      inputType: 'text',
      type: 'number',
      error: '',
    },
    ltcAdjustment: {
      title: 'LTC Adjustment',
      inputType: 'text',
      type: 'number',
      error: '',
    },
    ltpAdjustment: {
      title: 'LTP Adjustment',
      inputType: 'text',
      type: 'number',
      error: '',
    },
  }
  if (forPdf) {
    Object.keys(rlt).forEach((key) => {
      if (rlt[key].inputType.toLowerCase() === 'select') {
        rlt[key].inputType = 'text'
      }
    })
  }
  return rlt
}

export const fieldsByTransactionTypeAndProductTypeConstant: { [key: string]: { [key1: string]: string[] } } = {
  Bridge: {
    Purchase: [
      'lienType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'propertyPurchasePrice',
      'asIsValue',
      'interestReserve',
      'closingCostEstimate',
      'escrowRepairHoldback',
      'experience',
      'howManyExperiences',
      'propertyType',
      'numberOfUnits',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'propertyUnderConstruction',
      'isMultipleProperties',
      'isPropertyInLeasableState',
      'decliningMarketProperty',
      'isNewConstructionProperty',
      'ruralProperty',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'residency',
      'amortizationType',
      'monthsReserve',
      'aivLtvAdjustment',
      'arvLtvAdjustment',
      'ltcAdjustment',
      'ltpAdjustment',
    ],
    Refinance: [
      'lienType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'asIsValue',
      'interestReserve',
      'lienPayoff',
      'closingCostEstimate',
      'escrowRepairHoldback',
      'experience',
      'howManyExperiences',
      'propertyType',
      'numberOfUnits',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'isMultipleProperties',
      'propertyUnderConstruction',
      'isPropertyInLeasableState',
      'decliningMarketProperty',
      'isNewConstructionProperty',
      'ruralProperty',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'residency',
      'amortizationType',
      'monthsReserve',
      'aivLtvAdjustment',
      'arvLtvAdjustment',
      'ltcAdjustment',
      'ltpAdjustment',
    ],
  },
  'Fix and Flip': {
    Purchase: [
      'lienType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'propertyPurchasePrice',
      'asIsValue',
      'interestReserve',
      'closingCostEstimate',
      'experience',
      'howManyExperiences',
      'afterRepairValue',
      'rehabBudget',
      'constructionReserve',
      'propertyType',
      'numberOfUnits',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'changeToSquareFootage',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'residency',
      'amortizationType',
      'monthsReserve',
      'aivLtvAdjustment',
      'arvLtvAdjustment',
      'ltcAdjustment',
      'ltpAdjustment',
    ],
    Refinance: [
      'lienType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'asIsValue',
      'interestReserve',
      'lienPayoff',
      'closingCostEstimate',
      'experience',
      'howManyExperiences',
      'afterRepairValue',
      'rehabBudget',
      'constructionReserve',
      'propertyType',
      'numberOfUnits',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'changeToSquareFootage',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'residency',
      'amortizationType',
      'monthsReserve',
      'aivLtvAdjustment',
      'arvLtvAdjustment',
      'ltcAdjustment',
      'ltpAdjustment',
    ],
  },
  'Ground Up Construction': {
    Purchase: [
      'lienType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'propertyPurchasePrice',
      'asIsValue',
      'interestReserve',
      'closingCostEstimate',
      'experience',
      'howManyExperiences',
      'afterRepairValue',
      'rehabBudget',
      'constructionReserve',
      'propertyType',
      'numberOfUnits',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'residency',
      'amortizationType',
      'monthsReserve',
      'aivLtvAdjustment',
      'arvLtvAdjustment',
      'ltcAdjustment',
      'ltpAdjustment',
    ],
    Refinance: [
      'lienType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'asIsValue',
      'interestReserve',
      'lienPayoff',
      'closingCostEstimate',
      'experience',
      'howManyExperiences',
      'afterRepairValue',
      'rehabBudget',
      'constructionReserve',
      'propertyType',
      'numberOfUnits',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'residency',
      'amortizationType',
      'monthsReserve',
      'aivLtvAdjustment',
      'arvLtvAdjustment',
      'ltcAdjustment',
      'ltpAdjustment',
    ],
  },
  'DSCR (Long Term Rental)': {
    Purchase: [
      'lienType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'propertyPurchasePrice',
      'asIsValue',
      'closingCostEstimate',
      'escrowRepairHoldback',
      'propertyType',
      'numberOfUnits',
      'prepaymentPenalty',
      'residency',
      'amortizationType',
      'escrowType',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'isNewConstructionProperty',
      'isPropertyInLeasableState',
      'isShortTermRental',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'monthsReserve',
      'estimatedDscr',
      'proposedMonthlyRent',
      'proposedMonthlyTaxes',
      'proposedMonthlyInsurance',
      'proposedMonthlyHoaDues',
      'floodInsurance',
      'schoolTax',
      'otherTax',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'ltvAdjustment',
      'cltvAdjustment',
    ],
    Refinance: [
      'lienType',
      'proposedOccupancy',
      'loanTerm',
      'borrowerType',
      'proposedLoanAmount',
      'isBPiece',
      'aPieceAmount',
      'asIsValue',
      'lienPayoff',
      'closingCostEstimate',
      'escrowRepairHoldback',
      'propertyType',
      'numberOfUnits',
      'prepaymentPenalty',
      'propertyHasBeenListed',
      'residency',
      'amortizationType',
      'escrowType',
      'firstTimeHomeBuyer',
      'firstTimeHomeInvestor',
      'ruralProperty',
      'isMultipleProperties',
      'decliningMarketProperty',
      'isNewConstructionProperty',
      'isPropertyInLeasableState',
      'isShortTermRental',
      'bankruptcy',
      'foreclosure',
      'deedInLieu',
      'shortSale',
      'mortgageLates',
      'forbearanceLoanModification',
      'secondLien',
      'monthsReserve',
      'estimatedDscr',
      'proposedMonthlyRent',
      'proposedMonthlyTaxes',
      'proposedMonthlyInsurance',
      'proposedMonthlyHoaDues',
      'floodInsurance',
      'schoolTax',
      'otherTax',
      'borrowerFirstName',
      'borrowerMiddleName',
      'borrowerLastName',
      'isNoScore',
      'estimatedCreditScore',
      'propertyState',
      'subjectPropertyAddress',
      'additionalPropertyAddress',
      'subjectPropertyMap',
      'ltvAdjustment',
      'cltvAdjustment',
    ],
  },
}

export const groupedFields = [
  [
    'productType',
    'transactionType',
    'lienType',
    'propertyUnderConstruction',
    'isPropertyInLeasableState',
    'isShortTermRental',
    'decliningMarketProperty',
    'isNewConstructionProperty',
    'changeToSquareFootage',
    'isMultipleProperties',
    'howManyProperties',
    'isBPiece',
  ],
  ['proposedOccupancy', 'loanTerm', 'prepaymentPenalty', 'propertyHasBeenListed'],
  [
    'propertyPurchasePrice',
    'asIsValue',
    'afterRepairValue',
    'interestReserve',
    'secondLien',
    'secondLienPayment',
    'lienPayoff',
    'rehabBudget',
    'constructionReserve',
    'escrowRepairHoldback',
    'closingCostEstimate',
    'proposedLoanAmount',
    'aPieceAmount',
  ],
  ['experience', 'howManyExperiences'],
  ['amortizationType', 'escrowType'],
  ['isNoScore', 'propertyState', 'estimatedCreditScore', 'isCEMATransaction'],
  ['propertyType', 'condoType', 'numberOfUnits'],
  ['firstTimeHomeBuyer', 'firstTimeHomeInvestor', 'ruralProperty'],
  [
    'bankruptcy',
    'foreclosure',
    'deedInLieu',
    'shortSale',
    'mortgageLates',
    'forbearanceLoanModification',
    'residency',
    'monthsReserve',
  ],
  ['borrowerType', 'borrowerFirstName', 'borrowerMiddleName', 'borrowerLastName'],
  [
    'proposedMonthlyRent',
    'proposedMonthlyTaxes',
    'proposedMonthlyInsurance',
    'proposedMonthlyHoaDues',
    'floodInsurance',
    'schoolTax',
    'otherTax',
    'estimatedDscr',
  ],
  ['subjectPropertyAddress', 'additionalPropertyAddress', 'subjectPropertyMap'],
  ['ltvAdjustment', 'cltvAdjustment', 'aivLtvAdjustment', 'arvLtvAdjustment', 'ltcAdjustment', 'ltpAdjustment'],
]

export const OwnershipTypes = ['Own', 'Rent', 'No Primary Housing Expense']

export const YesNoOptions = {
  yes: 'Yes',
  no: 'No',
}

export const YearOptions = {
  '1': '1 year',
  '2': '2 years',
  '3': '3 years',
  '4': '4 years',
  '5': '5 years',
  '6': '6 years',
  '7': '7 years',
  '8': '8 years',
  '9': '9 years',
  '10': '10+ years',
}

export const OwnRentYearOptions = {
  '1': '< 2 Years',
  '2': '>= 2 Years',
}

export const OwnRentYearOptionsRev: Record<string, string> = Object.fromEntries(
  Object.entries(OwnRentYearOptions).map(([key, value]) => [value, key]),
)

export const MaritalStatusTypes: Record<string, string> = {
  Married: 'Married (includes registered domestic partners)',
  Unmarried: 'Unmarried (includes divorced, widow)',
  Single: 'Single (never been married)',
  Separated: 'Separated',
}

export const MaritalStatusTypesRev: Record<string, string> = Object.fromEntries(
  Object.entries(MaritalStatusTypes).map(([key, value]) => [value, key]),
)

export const priceLoanRelatedFields = [
  'transactionType',
  'productType',
  'lienType',
  'loanTerm',
  'proposedOccupancy',
  'proposedLoanAmount',
  'isBPiece',
  'aPieceAmount',
  'closingCostEstimate',
  'escrowRepairHoldback',
  'propertyPurchasePrice',
  'asIsValue',
  'afterRepairValue',
  'rehabBudget',
  'interestReserve',
  'constructionReserve',
  'escrowType',
  'propertyType',
  'numberOfUnits',
  'propertyHasBeenListed',
  'firstTimeHomeBuyer',
  'firstTimeHomeInvestor',
  'isMultipleProperties',
  'howManyProperties',
  'propertyUnderConstruction',
  'isPropertyInLeasableState',
  'isShortTermRental',
  'ruralProperty',
  'decliningMarketProperty',
  'isCEMATransaction',
  'isNewConstructionProperty',
  'changeToSquareFootage',
  'bankruptcy',
  'foreclosure',
  'deedInLieu',
  'shortSale',
  'mortgageLates',
  'forbearanceLoanModification',
  'secondLien',
  'secondLienPayment',
  'monthsReserve',
  'estimatedDscr',
  'condoType',
  'prepaymentPenalty',
  'residency',
  'amortizationType',
  'subjectPropertyAddress',
  'additionalPropertyAddress',
  'isNoScore',
  'estimatedCreditScore',
  'propertyState',
  'experience',
  'howManyExperiences',
  'borrowerType',
  'proposedMonthlyRent',
  'proposedMonthlyTaxes',
  'proposedMonthlyInsurance',
  'proposedMonthlyHoaDues',
  'floodInsurance',
  'schoolTax',
  'otherTax',
  'ltvAdjustment',
  'cltvAdjustment',
  'aivLtvAdjustment',
  'arvLtvAdjustment',
  'ltcAdjustment',
  'ltpAdjustment',
]

export const priceLimitRelatedFields = [
  'transactionType',
  'isNoScore',
  'estimatedCreditScore',
  'productType',
  'experience',
  'howManyExperiences',
  'proposedLoanAmount',
  'isBPiece',
  'aPieceAmount',
  'propertyPurchasePrice',
  'asIsValue',
  'afterRepairValue',
  'rehabBudget',
  'constructionReserve',
  'propertyHasBeenListed',
  'firstTimeHomeInvestor',
  'firstTimeHomeBuyer',
  'ruralProperty',
  'isMultipleProperties',
  'howManyProperties',
  'propertyUnderConstruction',
  'isPropertyInLeasableState',
  'isShortTermRental',
  'decliningMarketProperty',
  'isCEMATransaction',
  'isNewConstructionProperty',
  'lienPayoff',
  'closingCostEstimate',
  'escrowRepairHoldback',
  'interestReserve',
  'residency',
  'bankruptcy',
  'foreclosure',
  'deedInLieu',
  'shortSale',
  'mortgageLates',
  'forbearanceLoanModification',
  'propertyType',
  'subjectPropertyAddress',
  'proposedOccupancy',
  'ltvAdjustment',
  'cltvAdjustment',
  'aivLtvAdjustment',
  'arvLtvAdjustment',
  'ltcAdjustment',
  'ltpAdjustment',
]

export const calcLTVFields = [
  'transactionType',
  'propertyPurchasePrice',
  'asIsValue',
  'constructionReserve',
  'proposedLoanAmount',
  'isBPiece',
  'aPieceAmount',
  'isMultipleProperties',
  'howManyProperties',
]

export const calcCLTVFields = [...calcLTVFields, 'secondLien']

export const sandBoxNotEditableFields = [
  'borrowerType',
  'borrowerFirstName',
  'borrowerMiddleName',
  'borrowerLastName',
  // 'estimatedCreditScore',
  // 'proposedMonthlyTaxes',
  // 'proposedMonthlyInsurance',
  // 'proposedMonthlyRent',
  // 'proposedMonthlyHoaDues',
  // 'floodInsurance',
  // 'schoolTax',
  // 'otherTax',
]

export const dscrRelatedFields = [
  'productType',
  'proposedLoanAmount',
  'isBPiece',
  'aPieceAmount',
  'secondLien',
  'secondLienPayment',
  'proposedMonthlyInsurance',
  'proposedMonthlyTaxes',
  'proposedMonthlyHoaDues',
  'proposedMonthlyRent',
  'floodInsurance',
  'schoolTax',
  'otherTax',
]

export const MultiPropertyFields = [
  'subjectPropertyAddress',
  'proposedOccupancy',
  'propertyHasBeenListed',
  'propertyPurchasePrice',
  'asIsValue',
  'afterRepairValue',
  'rehabBudget',
  'constructionReserve',
  'secondLien',
  'secondLienPayment',
  'lienPayoff',
  'propertyType',
  'condoType',
  'numberOfUnits',
  'proposedMonthlyRent',
  'proposedMonthlyTaxes',
  'proposedMonthlyInsurance',
  'proposedMonthlyHoaDues',
  'floodInsurance',
  'schoolTax',
  'otherTax',
  'estimatedDscr',
]

export const propertySummaryFields = [
  'propertyPurchasePrice',
  'asIsValue',
  'afterRepairValue',
  'rehabBudget',
  'constructionReserve',
  'secondLien',
  'secondLienPayment',
  'lienPayoff',
  'proposedMonthlyRent',
  'proposedMonthlyTaxes',
  'proposedMonthlyInsurance',
  'proposedMonthlyHoaDues',
  'floodInsurance',
  'schoolTax',
  'otherTax',
]

export const DataVerifyTaxAvailableYears = Object.keys(Array(4).fill(1))
  .map((v) => new Date().getFullYear() + Number(v) - 4)
  .reverse()

export enum PipeLineType {
  Default = 'Default',
  Servicing = 'Servicing',
  Fci = 'FCI',
  Custom = 'Custom',
}

export const OrdinaryPipelineTypes = [PipeLineType.Default, PipeLineType.Custom]
